import { useCallback } from 'react'

export const AUTO_TIMEOUT_DURATION = 1000

export interface useCheckCameraPermissionReturnType {
  checkPermissionStatus: () => Promise<PermissionState | 'unknown'>
  initiateCameraAccess: (
    constraints?: MediaStreamConstraints,
  ) => Promise<MediaStream | void> | undefined
}

/**
 * Checks for camera permission status in a browser.
 */
export const useCheckCameraPermission = ({
  onAllowed,
  onRejected,
  onDismissed,
}: {
  onAllowed?: (method: 'manual' | 'auto') => void
  onRejected?: (method: 'manual' | 'auto') => void
  onDismissed?: () => void
}): useCheckCameraPermissionReturnType => {
  const checkPermissionStatus = useCallback(async () => {
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'camera' as PermissionName,
        })
        return permissionStatus.state // 'granted', 'denied', or 'prompt'
      } catch (error) {
        return 'unknown'
      }
    } else {
      // Attempt to use getUserMedia as a fallback to infer permission status.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        })
        // Immediately stop using the stream if it was successfully acquired.
        stream.getTracks().forEach((track) => track.stop())
        return 'granted'
      } catch (error) {
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError'
        ) {
          return 'denied'
        } else {
          return 'unknown'
        }
      }
    }
  }, [])

  const initiateCameraAccess = useCallback(
    (constraints: MediaStreamConstraints = { video: true }) => {
      let timeoutPassed = false
      setTimeout(() => {
        // Once 1 second has passed, assume an allow or reject action was manually
        // done by the user, instead of the system returning a previous permission
        // value.
        timeoutPassed = true
      }, AUTO_TIMEOUT_DURATION)

      if (
        navigator?.mediaDevices?.getUserMedia &&
        typeof navigator.mediaDevices.getUserMedia === 'function'
      ) {
        return navigator.mediaDevices
          .getUserMedia(constraints)
          .then((stream) => {
            if (stream.active) {
              onAllowed?.(timeoutPassed ? 'manual' : 'auto')
            }
            return stream
          })
          .catch((error) => {
            if (
              error.message === 'Permission denied' ||
              // for Safari
              error.name === 'NotAllowedError'
            ) {
              onRejected?.(timeoutPassed ? 'manual' : 'auto')
            }
            if (error.message === 'Permission dismissed') {
              onDismissed?.()
            }
          })
      }
    },
    [onAllowed, onRejected, onDismissed],
  )

  return { checkPermissionStatus, initiateCameraAccess }
}
